import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/Header';
import { ImageUpload } from '@/components/ImageUpload';
import { FormatSelector, FormatType } from '@/components/FormatSelector';
import { CreativeContextInput } from '@/components/CreativeContextInput';
import { OutputPanel } from '@/components/OutputPanel';
import { NoCreditsModal } from '@/components/NoCreditsModal';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fitImageToExactSizeIfNeeded } from '@/lib/fitImageToExactSize';
import { useIsMobile } from '@/hooks/use-mobile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface GeneratedImage {
  format: FormatType;
  url: string;
}

export default function Studio() {
  const [sourceImage, setSourceImage] = useState<File | null>(null);
  const [sourcePreview, setSourcePreview] = useState<string | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<FormatType[]>([]);
  const [creativeContext, setCreativeContext] = useState('');
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingFormats, setLoadingFormats] = useState<FormatType[]>([]);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [outputExpanded, setOutputExpanded] = useState(true);

  const formatPixelSize: Record<FormatType, { width: number; height: number }> = {
    square: { width: 1080, height: 1080 },
    portrait: { width: 1080, height: 1920 },
    horizontal: { width: 1920, height: 1080 },
  };
  
  const { profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleImageSelect = (file: File, preview: string) => {
    setSourceImage(file);
    setSourcePreview(preview);
    setGeneratedImages([]);
  };

  const handleClearImage = () => {
    setSourceImage(null);
    setSourcePreview(null);
    setGeneratedImages([]);
  };

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment-link');
      
      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      toast({
        title: 'Error creating payment link',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleGenerate = async () => {
    if (!sourceImage || selectedFormats.length === 0) {
      toast({
        title: 'Please select an image and at least one format',
        variant: 'destructive',
      });
      return;
    }

    const credits = profile?.credits ?? 0;
    if (credits < selectedFormats.length) {
      setShowNoCreditsModal(true);
      return;
    }

    setIsGenerating(true);
    setLoadingFormats([...selectedFormats]);
    setGeneratedImages([]);

    try {
      // Convert image to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(sourceImage);
      });
      const base64Image = await base64Promise;

      // Generate all formats in parallel
      const results = await Promise.allSettled(
        selectedFormats.map(async (format) => {
          const { data, error } = await supabase.functions.invoke('adapt-image', {
            body: {
              image: base64Image,
              format,
              context: creativeContext,
            },
          });
          if (error) throw error;
          return { format, data };
        })
      );

      // Process results
      for (const result of results) {
        if (result.status === 'fulfilled') {
          const { format, data } = result.value;
          if (data?.image) {
            const { width, height } = formatPixelSize[format];
            const normalized = await fitImageToExactSizeIfNeeded(data.image, width, height);
            setGeneratedImages((prev) => [...prev, { format, url: normalized }]);
          }
          setLoadingFormats((prev) => prev.filter((f) => f !== format));
        } else {
          // Extract format from the error context
          const failedFormat = selectedFormats[results.indexOf(result)];
          console.error(`Error generating ${failedFormat}:`, result.reason);
          setLoadingFormats((prev) => prev.filter((f) => f !== failedFormat));
          
          const errorMessage = result.reason?.message || '';
          const isTimeout = errorMessage.includes('fetch') || errorMessage.includes('abort');
          toast({
            title: isTimeout 
              ? `Request timed out for ${failedFormat}. Please try again.`
              : `Error generating ${failedFormat} format`,
            variant: 'destructive',
          });
        }
      }

      await refreshProfile();
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: 'Error generating images',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
      setLoadingFormats([]);
    }
  };

  const totalCost = selectedFormats.length;
  const canGenerate = sourceImage && selectedFormats.length > 0 && !isGenerating;

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      <Header onUpgrade={handleUpgrade} isUpgrading={isUpgrading} />
      
      <main className={`flex-1 container mx-auto px-4 py-4 overflow-hidden ${isMobile ? 'pb-20' : ''}`}>
        <div className={`${isMobile ? 'flex flex-col gap-4 h-full overflow-auto' : 'grid lg:grid-cols-2 gap-6 h-full'}`}>
          {/* Left Panel - Input */}
          <div className="flex flex-col gap-4 min-h-0">
            <div className="flex-shrink-0">
              <h2 className="text-lg font-semibold mb-2">Source Image</h2>
              <ImageUpload
                onImageSelect={handleImageSelect}
                preview={sourcePreview}
                onClear={handleClearImage}
              />
            </div>

            <FormatSelector
              selected={selectedFormats}
              onChange={setSelectedFormats}
              disabled={isGenerating}
            />

            <CreativeContextInput
              value={creativeContext}
              onChange={setCreativeContext}
              disabled={isGenerating}
              showSuggestions={!isMobile}
            />

            {/* Desktop Generate Button */}
            {!isMobile && (
              <Button
                onClick={handleGenerate}
                disabled={!canGenerate}
                className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold glow-primary flex-shrink-0"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Generate ({totalCost} {totalCost === 1 ? 'credit' : 'credits'})
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Right Panel - Output */}
          {isMobile ? (
            <Collapsible
              open={outputExpanded}
              onOpenChange={setOutputExpanded}
              className="bg-card border border-border rounded-xl overflow-hidden"
            >
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors">
                <h2 className="text-lg font-semibold">Output</h2>
                <div className="flex items-center gap-2">
                  {generatedImages.length > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {generatedImages.length} image{generatedImages.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  <ChevronDown 
                    className={cn(
                      "h-5 w-5 transition-transform duration-200",
                      !outputExpanded && "-rotate-90"
                    )} 
                  />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0">
                  <OutputPanel
                    images={generatedImages}
                    isLoading={isGenerating}
                    loadingFormats={loadingFormats}
                    hideTitle
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <div className="bg-card border border-border rounded-xl p-4 min-h-0 overflow-auto">
              <OutputPanel
                images={generatedImages}
                isLoading={isGenerating}
                loadingFormats={loadingFormats}
              />
            </div>
          )}
        </div>
      </main>

      {/* Mobile Fixed Generate Button */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
          <Button
            onClick={handleGenerate}
            disabled={!canGenerate}
            className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold glow-primary"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                Generate ({totalCost} {totalCost === 1 ? 'credit' : 'credits'})
              </>
            )}
          </Button>
        </div>
      )}

      <NoCreditsModal
        open={showNoCreditsModal}
        onOpenChange={setShowNoCreditsModal}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
}
